import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AICard } from "@/components/shared/AICard";
import { useApp } from "@/context/AppContext";
import { api } from "@/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, MessageCircle, Phone, Send, Sparkles, Loader2, Paperclip, X, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { useFileSelectConfirm, FileSelectConfirmBar } from "@/components/shared/FileSelectConfirm";

const CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "email", label: "Email", icon: Mail },
  { id: "call", label: "Call", icon: Phone },
];

export function Composer() {
  const { composer, closeComposer } = useApp();
  const qc = useQueryClient();
  const open = !!composer;
  const [channel, setChannel] = useState("whatsapp");
  const [party, setParty] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [integration, setIntegration] = useState(null);
  const [checkingIntegration, setCheckingIntegration] = useState(false);
  const [clientRequestId, setClientRequestId] = useState("");
  const [localAttachments, setLocalAttachments] = useState([]);
  const [attaching, setAttaching] = useState(false);

  const parties = composer?.parties || ["Customer"];
  // Item 4 — attachments passed in by the caller (e.g. "Send document" from
  // DocumentPanel) PLUS anything attached here directly in the composer.
  const allAttachments = [...(composer?.attachments || []), ...localAttachments];

  useEffect(() => {
    if (composer) {
      setChannel(composer.channel || "whatsapp");
      setParty(composer.defaultParty || parties[0]);
      setSubject(composer.subjectHint || "");
      setBody("");
      setIntegration(null);
      setLocalAttachments([]);
      attachFileConfirm.reset();
      setClientRequestId(globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composer]);

  const attachFile = async (file) => {
    if (!file) return;
    setAttaching(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", "Document");
    fd.append("jobId", "auto");
    if (composer?.customerId) fd.append("customerId", composer.customerId);
    // Bug fix (Aug 2026): this used to mislabel the Lead id as "inquiryId"
    // (a Lead is not an Inquiry) and, more importantly, a Lead-only
    // composer (see LeadWorkspace.jsx — no customerId exists yet for a
    // Lead) never gave the uploaded document ANY link back to the Lead at
    // all. The backend's outbound-attachment check
    // (_document_matches_customer in server.py) only ever matched by
    // customerId, so sending a message with this attachment always 403'd
    // for a Lead. /documents/upload now accepts leadId directly and
    // /inbox/messages matches attachments against it.
    if (composer?.leadId) fd.append("leadId", composer.leadId);
    try {
      const { data } = await api.post("/documents/upload", fd);
      setLocalAttachments((prev) => [...prev, { id: data.id, name: data.name }]);
      toast.success("File attached — it will be linked to this message");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not attach file"));
      throw e;
    } finally {
      setAttaching(false);
    }
  };
  const attachFileConfirm = useFileSelectConfirm(attachFile);
  const removeLocalAttachment = (id) => setLocalAttachments((prev) => prev.filter((a) => a.id !== id));

  useEffect(() => {
    if (!composer || !party) return;
    let active = true;
    setCheckingIntegration(true);
    api.get("/communications/integrations/status", {
      params: { customerId: composer.customerId, leadId: composer.leadId, partyHint: party },
    }).then(({ data }) => { if (active) setIntegration(data); })
      .catch(() => { if (active) setIntegration(null); })
      .finally(() => { if (active) setCheckingIntegration(false); });
    return () => { active = false; };
  }, [composer, party]);

  if (!open) return null;

  const channelStatus = integration?.channels?.[channel];
  const contextFound = Boolean(integration?.contextFound);
  const recipientReady = channel === "call" ? contextFound : Boolean(channelStatus?.recipientAvailable);
  const providerConnected = channel !== "call" && Boolean(channelStatus?.configured && channelStatus?.recipientAvailable);
  const attachmentDeliveryBlocked = channel === "whatsapp" && providerConnected && allAttachments.length > 0 && channelStatus?.supportsAttachments === false;
  const canDeliver = providerConnected && !attachmentDeliveryBlocked;
  const whatsappProviderLabel = channelStatus?.provider === "msg91_whatsapp"
    ? "MSG91 WhatsApp"
    : channelStatus?.provider === "meta_whatsapp_cloud" ? "Meta WhatsApp Cloud" : "WhatsApp provider";
  const staticWhatsappTemplate = channel === "whatsapp" && channelStatus?.provider === "msg91_whatsapp" && channelStatus?.usesComposerBody === false;
  const deliveryMode = canDeliver ? "send" : "record";
  const emailSubjectMissing = channel === "email" && canDeliver && !subject.trim();
  const actionLabel = channel === "call" ? "Log call outcome" : canDeliver
    ? staticWhatsappTemplate ? "Send WhatsApp template" : `Send ${channel === "email" ? "email" : "WhatsApp"}`
    : `Record ${channel === "email" ? "email" : "WhatsApp"}`;

  const draft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/ai/draft", {
        channel, party, customerId: composer?.customerId, intent: composer?.intent,
      });
      setBody(data.draft);
      toast.success("AI draft ready — review before sending");
    } catch (e) {
      toast.error("AI draft unavailable, please write manually");
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    if (!body.trim()) {
      toast.error(channel === "call" ? "Add the call outcome before logging" : "Add the communication text before recording");
      return;
    }
    setSending(true);
    try {
      let saved = null;
      if (composer?.customerId || composer?.leadId) {
        const response = await api.post("/inbox/messages", {
          customerId: composer.customerId, channel, dir: channel === "call" ? "out" : "out",
          leadId: composer.leadId, from: composer.companyName,
          subject: channel === "email" ? subject : null, body,
          attachments: allAttachments.map((a) => a.name),
          attachmentDocumentIds: allAttachments.map((a) => a.id).filter(Boolean),
          partyHint: party, deliveryMode, clientRequestId,
          outcome: channel === "call" ? "Logged" : null,
        });
        saved = response.data;
        qc.invalidateQueries({ queryKey: ["comms"] });
        qc.invalidateQueries({ queryKey: ["inbox"] });
      }
      const followUpWrites = [];
      if (composer?.leadId) {
        followUpWrites.push(api.post(`/leads/${composer.leadId}/contact-attempts`, { channel, note: body || subject || `${channel} outreach` }));
        qc.invalidateQueries({ queryKey: ["lead", composer.leadId] });
      }
      // Phase 4.5 Part 9: when this Composer was opened from an
      // AdvanceRequest context (Finance requesting a duty/VAT/prepayment
      // advance), also link the send to the real AdvanceRequest via the
      // existing finance_gate communications endpoint — same customer
      // communication history above, PLUS a record on the AdvanceRequest
      // itself (audited). No parallel messaging system.
      if (composer?.advanceRequestId) {
        followUpWrites.push(api.post(`/finance-gate/advance-requests/${composer.advanceRequestId}/communications`, { channel, recipient: party }));
        qc.invalidateQueries({ queryKey: ["fin-advances"] });
      }
      if (followUpWrites.length) await Promise.allSettled(followUpWrites);
      // Optional caller hook — fired only after a real save succeeds, so a
      // caller can record "this was actually sent" (e.g. Workspace.jsx's
      // ad-hoc quotation-sent marker) without the Composer needing to know
      // anything about what the caller is tracking.
      if (composer?.onSent) { try { await composer.onSent(saved); } catch { /* non-fatal to the send itself */ } }
      closeComposer();
      if (channel === "call") toast.success("Call outcome logged to customer history");
      else if (saved?.deliveryMode === "send") toast.success(`${channel === "email" ? "Email" : "WhatsApp"} accepted by the provider and logged`);
      else toast.success(`${channel === "email" ? "Email" : "WhatsApp"} recorded without external delivery`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save the communication"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeComposer()}>
      <DialogContent className="sm:max-w-lg" data-testid="composer">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Send className="h-4 w-4 text-indigo-600" /> Communicate
          </DialogTitle>
          <DialogDescription className="sr-only">Send through a configured provider or record a customer communication in the audit history.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-1.5">
            {CHANNELS.map((c) => (
              <button key={c.id} data-testid={`composer-channel-${c.id}`} onClick={() => setChannel(c.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${channel === c.id ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                <c.icon className="h-4 w-4" /> {c.label}
              </button>
            ))}
          </div>

          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Recipient / Party</label>
            <Select value={party} onValueChange={setParty}>
              <SelectTrigger className="mt-1" data-testid="composer-party"><SelectValue /></SelectTrigger>
              <SelectContent>{parties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {channel === "email" && <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="composer-subject" />}

          {allAttachments.length > 0 && (
            <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-2.5" data-testid="composer-attachments">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-500 mb-1.5">Attached documents</div>
              {allAttachments.map((a) => (
                <div key={a.id || a.name} className="flex items-center justify-between gap-2 text-sm text-indigo-900">
                  <span className="flex items-center gap-2 min-w-0"><Paperclip className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{a.name}</span></span>
                  {localAttachments.some((l) => l.id === a.id) && (
                    <button type="button" onClick={() => removeLocalAttachment(a.id)} data-testid={`composer-remove-attachment-${a.id}`} className="text-indigo-400 hover:text-indigo-700"><X className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <input ref={attachFileConfirm.inputRef} type="file" className="hidden" onChange={attachFileConfirm.onFileChosen} data-testid="composer-attach-file-input" />
            <Button type="button" variant="outline" size="sm" className="h-8" disabled={attaching} onClick={attachFileConfirm.openPicker} data-testid="composer-attach-file-btn">
              {attaching ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5 mr-1.5" />}
              {attaching ? "Attaching…" : "Attach file"}
            </Button>
            <FileSelectConfirmBar pending={attachFileConfirm.pending} busy={attachFileConfirm.busy} onSubmit={attachFileConfirm.submit} onCancel={attachFileConfirm.cancel} onReUpload={attachFileConfirm.reUpload} submitLabel="Submit attachment" />
          </div>

          <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)}
            placeholder={channel === "call" ? "Log the call outcome, notes and follow-up commitment…" : "Write a message…"} data-testid="composer-body" />

          <button onClick={draft} disabled={drafting} className="w-full disabled:opacity-70" data-testid="composer-draft">
            <AICard title="Writing assistant" className="text-left cursor-pointer hover:bg-violet-100 transition-colors">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700">
                {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {drafting ? "Drafting…" : `Draft a ${channel === "call" ? "call summary" : "reply"} for ${party}`}
              </span>
            </AICard>
          </button>
        </div>

        <div className="space-y-2 pt-1">
          <div data-testid="composer-delivery-status" className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${
            checkingIntegration ? "border-slate-200 bg-slate-50 text-slate-500"
              : canDeliver ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : recipientReady ? "border-amber-200 bg-amber-50 text-amber-800"
                  : "border-rose-200 bg-rose-50 text-rose-800"
          }`}>
            {checkingIntegration ? <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
              : canDeliver ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                : <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <span>
              {checkingIntegration ? "Checking delivery connection and recipient…"
                : !contextFound ? "A valid customer or lead record is required before this can be saved."
                  : channel === "call" ? channelStatus?.recipientAvailable
                    ? `Use Open phone app to call +${channelStatus.recipient?.address}, then record the outcome here for the audit history.`
                    : "No valid phone number is stored. You can still record an outcome for a call handled outside the dashboard."
                    : !channelStatus?.recipientAvailable ? `Add a valid ${channel === "email" ? "email address" : "WhatsApp number"} to this customer or lead first.`
                      : attachmentDeliveryBlocked ? "The configured MSG91 WhatsApp template cannot send attachments. Remove the attachments to send, or record this communication without external delivery."
                      : canDeliver ? staticWhatsappTemplate
                        ? `${whatsappProviderLabel} is connected. The approved “${channelStatus.templateName}” template will be sent to ${channelStatus.recipient?.address}; the text entered here is retained as an audit note.`
                        : `${channel === "email" ? "SMTP" : whatsappProviderLabel} is connected. This will send to ${channelStatus.recipient?.address}.`
                        : `${channel === "email" ? "Email" : "WhatsApp"} delivery is not configured. This will create an audited record only; nothing will be sent externally.`}
              {emailSubjectMissing && <span className="block mt-0.5 font-medium">Add an email subject before sending.</span>}
            </span>
          </div>
          <div className="flex items-center justify-end gap-2">
            {channel === "call" && channelStatus?.recipientAvailable && (
              <Button asChild variant="outline" className="mr-auto" data-testid="composer-launch-call">
                <a href={`tel:+${channelStatus.recipient.address}`}><Phone className="h-3.5 w-3.5 mr-1.5" />Open phone app</a>
              </Button>
            )}
            <Button variant="ghost" onClick={closeComposer} data-testid="composer-cancel"><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>
            <Button onClick={send} disabled={sending || checkingIntegration || !recipientReady || !body.trim() || emailSubjectMissing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="composer-send">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              {actionLabel} <Send className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
